/**
 * Лінійка під курсором: смуга доби й смуга рядка поверх таблиці.
 *
 * Спільна для всіх таблиць місяця — харчування, нарядів, статусу й рапортів:
 * підсвітка має поводитись однаково, де б людина не працювала. Таблиця лише
 * вішає `ref` на свої частини й кличе `moveRuler` з клітинки під курсором;
 * клітинка мусить мати `data-d`, а заголовок дня — `th.day[data-day]`.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { IsoDate } from '@kancelaria/shared';

export function useRuler(
  enabled: boolean,
  /** Коли міняється — таблиця перемалювалась, слухача прокрутки чіпляємо наново. */
  resetKey: unknown,
) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const colRef = useRef<HTMLDivElement | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const headRef = useRef<HTMLTableSectionElement | null>(null);
  const footRef = useRef<HTMLTableSectionElement | null>(null);
  const litHead = useRef<Element[]>([]);
  /** Остання підсвічена доба й рядок: рух усередині них нічого не змінює. */
  const lastSpot = useRef<{ date: string | null; row: HTMLElement | null }>({
    date: null,
    row: null,
  });
  /**
   * Розміри таблиці й заголовків днів. Під час водіння мишею вони не міняються,
   * а кожне читання `offsetWidth` змушує браузер перерахувати розкладку всієї
   * таблиці — на місяці це помітно гальмувало саму лінійку.
   */
  const geom = useRef<{ h: number; w: number; days: Map<string, [number, number]> } | null>(null);
  /** Куди лінійка має переїхати; застосуємо одним кадром, а не на кожен рух. */
  const wanted = useRef<{ cell: HTMLElement | null; date: IsoDate | null }>({
    cell: null,
    date: null,
  });
  const frame = useRef(0);
  /** Поки таблиця прокручується, лінійку не чіпаємо. */
  const scrolling = useRef(false);

  // Розкладка змінилася — виміри доведеться зняти наново.
  useEffect(() => {
    geom.current = null;
  });
  useEffect(() => {
    const drop = (): void => {
      geom.current = null;
    };
    window.addEventListener('resize', drop);
    return () => window.removeEventListener('resize', drop);
  }, []);

  /**
   * Підсвітка доби й рядка. Раніше вона писалася правилом у <style>, і браузер
   * на кожен рух миші перераховував стилі всієї таблиці — на тисячах клітинок
   * це з'їдало прокрутку. Тепер це дві накладки, які ми просто пересуваємо.
   */
  const drawRuler = useCallback((): void => {
    const { cell, date } = wanted.current;
    const col = colRef.current;
    const row = rowRef.current;
    if (!col || !row) return;

    const trNow = (cell?.parentElement as HTMLElement | null) ?? null;
    if (lastSpot.current.date === date && lastSpot.current.row === trNow) return;
    lastSpot.current = { date, row: trNow };

    for (const el of litHead.current) el.classList.remove('lit');
    litHead.current = [];

    if (!cell || !date || !enabled) {
      col.style.display = 'none';
      row.style.display = 'none';
      return;
    }

    // Між подією й кадром рядок міг зникнути — таблицю перемалював фільтр
    // або зміна місяця. Тоді просто ховаємо лінійку.
    const tr = cell.parentElement as HTMLElement | null;
    const table = tableRef.current;
    if (!tr || !table) {
      col.style.display = 'none';
      row.style.display = 'none';
      return;
    }

    // Смуга накриває всю добу, а не один прийом їжі: ширину беремо із
    // заголовка дня — він і в харчуванні, і в нарядах займає рівно добу.
    let box = geom.current;
    if (!box) {
      const map = new Map<string, [number, number]>();
      for (const th of headRef.current?.querySelectorAll<HTMLElement>('th.day[data-day]') ?? []) {
        map.set(th.dataset.day!, [th.offsetLeft, th.offsetWidth]);
      }
      box = { h: table.offsetHeight, w: table.offsetWidth, days: map };
      geom.current = box;
    }
    const span = box.days.get(date) ?? [cell.offsetLeft, cell.offsetWidth];
    col.style.display = 'block';
    col.style.left = `${span[0]}px`;
    col.style.width = `${span[1]}px`;
    col.style.height = `${box.h}px`;
    row.style.display = 'block';
    row.style.top = `${tr.offsetTop}px`;
    row.style.height = `${tr.offsetHeight}px`;
    row.style.width = `${box.w}px`;

    // Липкі комірки класом, решту — накладками: це десятки елементів, не тисячі.
    // Шукаємо всередині шапки й підсумків, а не по всій таблиці: пошук по
    // ній обходив би всі клітинки місяця на кожен рух миші.
    const lit: Element[] = [];
    const pick = (root: Element | null, sel: string): void => {
      if (root) lit.push(...root.querySelectorAll(sel));
    };
    pick(headRef.current, `th[data-day="${date}"], th[data-d="${date}"]`);
    pick(footRef.current, `td[data-d="${date}"]`);
    lit.push(...tr.querySelectorAll('.sticky, .c-tot, .c-sum'));
    litHead.current = lit;
    for (const el of lit) el.classList.add('lit');
  }, [enabled]);

  /**
   * Запит на переміщення лінійки.
   *
   * Малюємо її наступним кадром, а не просто в обробнику: щоб поставити смуги,
   * треба спитати в браузера розміри рядка, а він заради відповіді перераховує
   * розкладку всієї таблиці. Робити це просто в події означало робити просто
   * посеред прокрутки — звідси й ривки. Під час самої прокрутки лінійку взагалі
   * не рухаємо: вона й так їде разом із таблицею й лишається на своєму рядку.
   */
  const moveRuler = useCallback(
    (cell: HTMLElement | null, date: IsoDate | null): void => {
      if (scrolling.current && cell) return;
      wanted.current = { cell, date };
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        drawRuler();
      });
    },
    [drawRuler],
  );

  useEffect(
    () => () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    },
    [],
  );

  // Прокрутка: доки вона триває, лінійка мовчить. `passive` — щоб обробник не
  // мав права затримати сам скрол.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    let idle: ReturnType<typeof setTimeout>;
    const onScroll = (): void => {
      scrolling.current = true;
      clearTimeout(idle);
      idle = setTimeout(() => {
        scrolling.current = false;
      }, 120);
    };
    wrap.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      wrap.removeEventListener('scroll', onScroll);
      clearTimeout(idle);
    };
  }, [resetKey]);

  return { wrapRef, colRef, rowRef, tableRef, headRef, footRef, moveRuler };
}
